import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import createAxios from "@/lib/axios";
import { Card } from "@/components/ui/card";
import { useEffect, useState } from "react";

type Unit = { id: number; name: string };
type PresentationStatus = "processing" | "ppt_generated" | "completed";

export const Route = createFileRoute("/dashboard/material/new/select-unit")({
  component: CheckboxReactHookFormMultiple,
  validateSearch: (search) =>
    z
      .object({
        subject_id: z.number(),
      })
      .parse(search),
  loaderDeps: ({ search }) => ({ subject_id: search.subject_id }),
  loader: async ({ deps: { subject_id } }) => {
    const client = await createAxios();
    const unitsRes = await client.get<{ units: Unit[] }>(
      `${import.meta.env.VITE_BACKEND_URL}/subjects/${subject_id}/units`,
    );
    const units = unitsRes.data.units;
    if (!units) {
      throw new Error("No units found");
    }
    // convert unit.id to string
    return {
      units: units.map((unit) => ({
        ...unit,
        id: unit.id.toString(),
      })),
      subject_id,
    };
  },
});

const FormSchema = z.object({
  units: z.array(z.string()).refine((value) => value.some((unit) => unit), {
    message: "You have to select at least one unit.",
  }),
});

function CheckboxReactHookFormMultiple() {
  const { units } = Route.useLoaderData();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [presentationId, setPresentationId] = useState<string | null>(null);
  console.log(presentationId);
  const [presentationStatus, setPresentationStatus] =
    useState<PresentationStatus | null>(null);
  console.log(presentationStatus);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      units: [],
    },
  });

  useEffect(() => {
    let intervalId: number | null = null;
    if (presentationId) {
      intervalId = window.setInterval(async () => {
        try {
          const client = await createAxios();
          const response = await client.get(
            `${import.meta.env.VITE_BACKEND_URL}/presentation/${presentationId}`,
          );

          console.log("Presentation status response:", response.data);

          if (response.data && response.data.status) {
            setPresentationStatus(response.data.status);

            if (
              response.data.status === "ppt_generated" ||
              response.data.status === "completed"
            ) {
              if (intervalId) clearInterval(intervalId);
              toast.success("Presentation has been generated!");
              navigate({ to: `/dashboard/material/${presentationId}` });
            }
          } else {
            console.error("Invalid response format:", response.data);
          }
        } catch (error) {
          if (intervalId) clearInterval(intervalId);
          setIsLoading(false);
          toast.error("Failed to get presentation status. Please try again.");
          throw error;
        }
      }, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [presentationId, navigate]);
  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      setIsLoading(true);
      const uniqueUnits = Array.from(new Set(data.units)).sort();
      const requestBody = {
        units: uniqueUnits,
      };

      const client = await createAxios();
      const response = await client.post(
        `${import.meta.env.VITE_BACKEND_URL}/presentation`,
        requestBody,
      );

      console.log("Response Data /presentation :- ", response.data);
      const materialId = response.data.id || response.data._id;
      setPresentationId(materialId);
      toast.success("Creating your presentation. Please wait...");
      navigate({ to: "/dashboard/material/$id", params: { id: materialId } });
    } catch (error) {
      toast.error("Failed to create material. Please try again.");
      setIsLoading(false);
      throw error;
    }
  }

  return (
    <main>
      {isLoading && presentationStatus === "processing" ? (
        <div className="flex flex-col items-center justify-center space-y-4 p-8">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <h2 className="text-xl font-semibold">
            Generating your presentation...
          </h2>
          <p className="text-muted-foreground">This may take a few minutes</p>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="units"
              render={() => (
                <FormItem>
                  <div className="mb-4">
                    <FormLabel className="text-4xl">Select Units</FormLabel>
                    <FormDescription>
                      Select the units you want to generate course material for.
                    </FormDescription>
                  </div>
                  <div className="columns-2 space-y-4">
                    {units.map((unit) => (
                      <FormField
                        key={unit.id}
                        control={form.control}
                        name="units"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={unit.id}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <Card
                                onClick={() =>
                                  field.value?.includes(unit.id)
                                    ? field.onChange([...field.value, unit.id])
                                    : field.onChange(
                                        field.value?.filter(
                                          (value) => value !== unit.id,
                                        ),
                                      )
                                }
                                className="flex w-full cursor-pointer flex-row items-center gap-2 p-4"
                              >
                                <FormControl>
                                  <Checkbox
                                    className="border-black data-[state=checked]:bg-primary-foreground data-[state=checked]:text-primary"
                                    checked={field.value?.includes(unit.id)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([
                                            ...field.value,
                                            unit.id,
                                          ])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== unit.id,
                                            ),
                                          );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel
                                  title={unit.name}
                                  className="w-full cursor-pointer select-none text-ellipsis text-sm font-normal"
                                >
                                  {unit.name.length > 60
                                    ? unit.name.slice(0, 60) + "..."
                                    : unit.name}
                                </FormLabel>
                              </Card>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              disabled={form.formState.isSubmitting || isLoading}
            >
              {(form.formState.isSubmitting || isLoading) && (
                <Loader2 className="mr-2 animate-spin" />
              )}
              {form.formState.isSubmitting || isLoading
                ? "Processing..."
                : "Submit"}
            </Button>
          </form>
        </Form>
      )}
    </main>
  );
}
